import { Injectable, signal, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CartItem, CartState, CartApiResponse } from '../models/cart.models';
import { Product } from '../models/admin.models';
import { BehaviorSubject, Observable, map, firstValueFrom } from 'rxjs';
import { AuthService } from './auth.service';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class CartService {
  private authService = inject(AuthService);
  private http = inject(HttpClient);
  
  private cartState = signal<CartState>({
    items: [],
    totalItems: 0,
    subtotal: 0,
    taxTotal: 0,
    shippingTotal: 0
  });

  private cartState$ = new BehaviorSubject<CartState>(this.cartState());
  private currentUserId: string | null = null;
  private backendUrl = environment.backendUrl;
  private readonly GUEST_CART_KEY = 'aarvya_guest_cart';

  constructor() {
    this.authService.userProfile$.subscribe(user => {
      const newUserId = user?.uid || null;
      
      if (newUserId !== this.currentUserId) {
        if (newUserId) {
          // If we had a guest cart, merge it
          const guestItems = this.getGuestItems();
          if (guestItems.length > 0) {
            console.log('Merging guest cart into user account...');
            this.mergeGuestCart(newUserId, guestItems);
          } else {
            this.currentUserId = newUserId;
            this.loadCart(newUserId);
          }
        } else {
          // User logged out
          this.currentUserId = null;
          this.clearCartState();
          // Load guest cart if any
          this.loadGuestCart();
        }
      }
    });

    const currentUser = this.authService.currentUserValue;
    if (currentUser) {
       this.currentUserId = currentUser.uid;
       this.loadCart(currentUser.uid);
    } else {
       this.loadGuestCart();
    }
  }

  // Helper to map API response to internal state
  private mapResponseToState(response: CartApiResponse): CartState {
    const items = response.items.map(item => ({
      quantity: item.quantity,
      addedAt: new Date(), // API might not return this, using current time fallback
      product: {
        id: item.productId,
        name: item.productSnapshot.name,
        price: item.productSnapshot.price,
        discount: item.productSnapshot.discount || 0,
        image: item.productSnapshot.image,
        description: item.productSnapshot.description,
        category: item.productSnapshot.category,
        isInStock: item.stockStatus !== 'out_of_stock',
        rating: 0,
        reviewCount: 0,
        stockCount: item.currentStock,
        ingredients: '',
        howToUse: '',
        tags: [],
        sku: '' 
      } as Product
    }));

    const calculatedSubtotal = items.reduce((total, item) => {
      const price = item.product.discount 
        ? item.product.price * (1 - item.product.discount / 100) 
        : item.product.price;
      return total + (price * item.quantity);
    }, 0);

    return {
      items,
      totalItems: response.summary.itemCount,
      subtotal: calculatedSubtotal, // Use calculated subtotal to reflect discounts
      taxTotal: 0, // GST is 0, included in MRP
      shippingTotal: (calculatedSubtotal > 0 && calculatedSubtotal < 499) ? 49 : 0
    };
  }

  private updateState(newState: CartState) {
    this.cartState.set(newState);
    // Explicitly emit the new state to the BehaviorSubject
    this.cartState$.next(newState);
  }

  // --- API OPERATIONS ---

  private loadCart(userId: string) {
    this.http.get<CartApiResponse>(`${this.backendUrl}/users/${userId}/cart`).subscribe({
      next: (response) => {
        this.updateState(this.mapResponseToState(response));
      },
      error: (err) => {
        console.error('Failed to load cart', err);
        // If 404, usually means empty cart for new user, so clear state
        if (err.status === 404) {
           this.clearCartState();
        }
      }
    });
  }

  // Refresh cart after any mutation to ensure sync with server calculations
  refreshCart() {
    if (this.currentUserId) {
      this.loadCart(this.currentUserId);
    }
  }

  addToCart(product: Product, quantity: number = 1) {
    if (!this.currentUserId) {
       this.addToGuestCart(product, quantity);
       return;
    }

    const payload = {
      productId: product.id,
      quantity: quantity
    };

    this.http.post(`${this.backendUrl}/users/${this.currentUserId}/cart/items`, payload).subscribe({
      next: () => {
        this.refreshCart();
      },
      error: (err) => console.error('Failed to add item to cart', err)
    });
  }

  updateQuantity(productId: string, quantity: number) {
    if (!this.currentUserId) {
      this.updateGuestQuantity(productId, quantity);
      return;
    }

    if (quantity <= 0) {
      this.removeFromCart(productId);
      return;
    }

    const payload = { quantity };
    this.http.put(`${this.backendUrl}/users/${this.currentUserId}/cart/items/${productId}`, payload).subscribe({
      next: () => this.refreshCart(),
      error: (err) => console.error('Failed to update quantity', err)
    });
  }

  removeFromCart(productId: string) {
    if (!this.currentUserId) {
      this.removeFromGuestCart(productId);
      return;
    }

    this.http.delete(`${this.backendUrl}/users/${this.currentUserId}/cart/items/${productId}`).subscribe({
      next: () => this.refreshCart(),
      error: (err) => console.error('Failed to remove item', err)
    });
  }
  
  clearCart() {
     // Always clear localStorage guest cart to prevent lingering items
     localStorage.removeItem(this.GUEST_CART_KEY);
     
     if (!this.currentUserId) {
       this.clearCartState();
       return;
     }
     
     this.http.delete(`${this.backendUrl}/users/${this.currentUserId}/cart`).subscribe({
        next: () => this.clearCartState(),
        error: (err) => console.error('Failed to clear cart', err)
     });
  }

  // --- GUEST CART LOGIC ---

  private getGuestItems(): CartItem[] {
    const saved = localStorage.getItem(this.GUEST_CART_KEY);
    return saved ? JSON.parse(saved) : [];
  }

  private saveGuestItems(items: CartItem[]) {
    localStorage.setItem(this.GUEST_CART_KEY, JSON.stringify(items));
    this.updateGuestState(items);
  }

  private loadGuestCart() {
    this.updateGuestState(this.getGuestItems());
  }

  private addToGuestCart(product: Product, quantity: number) {
    const items = this.getGuestItems();
    const existing = items.find(i => i.product.id === product.id);
    if (existing) {
      existing.quantity += quantity;
    } else {
      items.push({ product, quantity, addedAt: new Date() });
    }
    this.saveGuestItems(items);
  }

  private updateGuestQuantity(productId: string, quantity: number) {
    if (quantity <= 0) {
      this.removeFromGuestCart(productId);
      return;
    }
    const items = this.getGuestItems();
    const existing = items.find(i => i.product.id === productId);
    if (existing) {
      existing.quantity = quantity;
      this.saveGuestItems(items);
    }
  }

  private removeFromGuestCart(productId: string) {
    const items = this.getGuestItems().filter(i => i.product.id !== productId);
    this.saveGuestItems(items);
  }

  private updateGuestState(items: CartItem[]) {
    const subtotal = items.reduce((total, item) => {
      const price = item.product.discount 
        ? item.product.price * (1 - item.product.discount / 100) 
        : item.product.price;
      return total + (price * item.quantity);
    }, 0);

    this.updateState({
      items,
      totalItems: items.reduce((sum, item) => sum + item.quantity, 0),
      subtotal: subtotal,
      taxTotal: 0, // GST included in MRP
      shippingTotal: (subtotal > 0 && subtotal < 499) ? 49 : 0
    });
  }

  private async mergeGuestCart(userId: string, guestItems: CartItem[]) {
    try {
      // Seqential add for simplicity, though could be optimized
      for (const item of guestItems) {
        await firstValueFrom(this.http.post(`${this.backendUrl}/users/${userId}/cart/items`, {
          productId: item.product.id,
          quantity: item.quantity
        }));
      }
      localStorage.removeItem(this.GUEST_CART_KEY);
      this.currentUserId = userId;
      this.loadCart(userId);
    } catch (error) {
      console.error('Failed to merge guest cart', error);
      // Fallback to just loading user cart
      this.currentUserId = userId;
      this.loadCart(userId);
    }
  }

  // Only updates local state (e.g. on logout)
  private clearCartState() {
     this.updateState({
      items: [],
      totalItems: 0,
      subtotal: 0,
      taxTotal: 0,
      shippingTotal: 0
    });
  }

  // --- GETTERS (No changes needed here usually, but good to keep clean) ---

  getCartState(): Observable<CartState> {
    return this.cartState$.asObservable();
  }

  getCartItems(): Observable<CartItem[]> {
    return this.cartState$.pipe(map(state => state.items));
  }

  getCartItemCount(): Observable<number> {
    return this.cartState$.pipe(map(state => state.totalItems));
  }

  getCartSubtotal(): Observable<number> {
    return this.cartState$.pipe(map(state => state.subtotal));
  }

  getCartTaxTotal(): Observable<number> {
    return this.cartState$.pipe(map(state => state.taxTotal));
  }

  getCartShippingTotal(): Observable<number> {
    return this.cartState$.pipe(map(state => state.shippingTotal));
  }

  getCartGrandTotal(): Observable<number> {
    return this.cartState$.pipe(
      map(state => state.subtotal + state.taxTotal + state.shippingTotal)
    );
  }
}
